import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Collapsible,
  Divider,
  InlineGrid,
  InlineStack,
  List,
  Page,
  Text,
} from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import type { MerchantProfile } from '../lib/types';

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {title}
        </Text>
        {children}
      </BlockStack>
    </Card>
  );
}

function FaqItem({ id, question, children }: { id: string; question: string; children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button
        variant="plain"
        textAlign="left"
        fullWidth
        disclosure={open ? 'up' : 'down'}
        onClick={() => setOpen((o) => !o)}
      >
        {question}
      </Button>
      <Collapsible id={id} open={open} transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}>
        <Box paddingBlock="200">
          <Text as="p" tone="subdued">
            {children}
          </Text>
        </Box>
      </Collapsible>
    </Box>
  );
}

const GAMES: Array<{ emoji: string; name: string; desc: string }> = [
  { emoji: '🎡', name: 'Spin Wheel', desc: 'A rotating wheel that lands on the won prize.' },
  { emoji: '🎟️', name: 'Scratch Card', desc: 'A foil cover that wipes away to reveal the prize.' },
  { emoji: '🎁', name: 'Lucky Box', desc: 'Pick one of several boxes to reveal a reward.' },
  { emoji: '📦', name: 'Gift Box', desc: 'Tap a gift box to open it.' },
  { emoji: '❓', name: 'Mystery Gift', desc: 'A suspenseful reveal of a surprise prize.' },
];

export function HelpPage(): JSX.Element {
  const navigate = useNavigate();
  const profile = useApi(() => apiClient.get<MerchantProfile>('/merchant/profile'), []);
  const shop = profile.data?.shopDomain;
  const themeEditorUrl = shop
    ? `https://${shop}/admin/themes/current/editor?context=apps`
    : null;
  const openThemeEditor = (): void => {
    if (themeEditorUrl) window.open(themeEditorUrl, '_blank', 'noopener');
  };

  return (
    <Page
      title="Help & Guide"
      subtitle="Everything you need to set up and run PlayForge"
      secondaryActions={[{ content: 'Contact support', onAction: () => navigate('/support') }]}
    >
      <BlockStack gap="400">
        <Banner tone="warning" title="First, turn PlayForge on in your theme">
          <BlockStack gap="200">
            <Text as="p">
              PlayForge won&apos;t appear on your storefront until you enable its <b>app embed</b> in
              your theme — a <b>one-time</b> step, separate from publishing a campaign.
            </Text>
            <List type="number">
              <List.Item>
                Open <b>Online Store → Themes → Customize</b> (button below).
              </List.Item>
              <List.Item>
                Click the <b>App embeds</b> icon (the puzzle piece) near the bottom of the left panel.
              </List.Item>
              <List.Item>
                Toggle <b>PlayForge</b> on, then click <b>Save</b>.
              </List.Item>
            </List>
            <InlineStack>
              <Button variant="primary" onClick={openThemeEditor} disabled={!themeEditorUrl}>
                Open theme editor
              </Button>
            </InlineStack>
          </BlockStack>
        </Banner>

        <Section title="What is PlayForge?">
          <Text as="p" tone="subdued">
            PlayForge turns your storefront into an interactive, prize-winning experience. Shoppers
            play a game, win a reward, and receive a real Shopify discount code they can redeem at
            checkout — while you capture emails, drive conversions, and track everything in a live
            dashboard.
          </Text>
        </Section>

        <Section title="Getting started">
          <List type="number">
            <List.Item>
              <b>Enable the app embed</b> — turn PlayForge on in your theme (see the banner above).
              Do this once.
            </List.Item>
            <List.Item>
              <b>Create a campaign</b> — go to Campaigns → Create campaign and name it.
            </List.Item>
            <List.Item>
              <b>Pick a game</b> — open the campaign, choose a game type in the Game tab.
            </List.Item>
            <List.Item>
              <b>Add prizes</b> — in Rewards, add outcomes and set each one&apos;s weight (win
              chance) and optional inventory.
            </List.Item>
            <List.Item>
              <b>Set rules</b> — choose pages, audience, and how often a shopper can play.
            </List.Item>
            <List.Item>
              <b>Style it</b> — under Themes, set colors, launcher position, and confetti.
            </List.Item>
            <List.Item>
              <b>Publish</b> — publish from the campaign&apos;s Publish tab. A launcher button
              appears on your store.
            </List.Item>
          </List>
          <InlineStack gap="200">
            <Button variant="primary" onClick={() => navigate('/campaigns')}>
              Go to Campaigns
            </Button>
            <Button onClick={() => navigate('/themes')}>Open Themes</Button>
          </InlineStack>
        </Section>

        <Section title="The games">
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
            {GAMES.map((g) => (
              <Box key={g.name} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                <BlockStack gap="100">
                  <Text as="span" variant="headingSm">
                    {g.emoji} {g.name}
                  </Text>
                  <Text as="span" tone="subdued" variant="bodySm">
                    {g.desc}
                  </Text>
                </BlockStack>
              </Box>
            ))}
          </InlineGrid>
        </Section>

        <Section title="Rewards & discounts">
          <Text as="p" tone="subdued">
            Each prize has a <b>weight</b> (its win chance = weight ÷ total) and optional{' '}
            <b>inventory</b> (it stops being awarded once depleted). The outcome is decided on the
            server, so it can&apos;t be tampered with from the browser.
          </Text>
          <Text as="p" tone="subdued">
            When a shopper wins a discount (percentage, fixed amount, free shipping, or free
            product), PlayForge creates a <b>real Shopify discount code</b> that is restricted to
            that shopper and limited to <b>one total use</b>.
          </Text>
        </Section>

        <Section title="Email capture & one play per shopper">
          <Text as="p" tone="subdued">
            Guests enter an email before playing; logged-in shoppers are recognized automatically.
            The server allows <b>one play per email</b> (or per customer) — this is enforced
            server-side, so clearing the browser does not grant another play. The captured email
            also becomes a <b>Shopify customer</b>, so you build your customer list as people play.
          </Text>
        </Section>

        <Section title="Customer reward wallet (“My Rewards”)">
          <Text as="p" tone="subdued">
            Logged-in shoppers get a <b>My Rewards</b> page in their account listing every coupon
            they&apos;ve won — each with a show/copy code, status (<Badge tone="success">active</Badge>{' '}
            <Badge tone="info">redeemed</Badge> <Badge>expired</Badge>), and expiry date. Shoppers can
            filter by status and the list is sorted by soonest expiry. It works on both account types:
          </Text>
          <List>
            <List.Item>
              <b>New customer accounts</b> — a <b>My Rewards</b> page is added to the account menu
              automatically. Enable the extension and set its <b>API base URL</b> to your app URL.
            </List.Item>
            <List.Item>
              <b>Classic customer accounts</b> — add the <b>PlayForge Rewards</b> block to the customer
              <i> Account</i> template (Online Store → Themes → Customize → Account template → add block).
            </List.Item>
          </List>
          <Text as="p" tone="subdued">
            A shopper only ever sees their own coupons — identity is verified server-side. Coupons won
            as a guest appear once the shopper logs in with the same email.
          </Text>
        </Section>

        <Section title="Analytics, customers & coupons">
          <List>
            <List.Item>
              <b>Analytics</b> — plays, completion rate, rewards won, coupons created/used, and a
              conversion funnel.
            </List.Item>
            <List.Item>
              <b>Customers</b> — each player&apos;s plays, prizes won, redemptions, and spend.
            </List.Item>
            <List.Item>
              <b>Coupons</b> — every won code with its status and usage. Use <b>Sync orders</b> to
              reconcile redemptions from Shopify (Analytics & Customers also sync on load).
            </List.Item>
          </List>
        </Section>

        <Section title="Plans & support">
          <Text as="p" tone="subdued">
            Manage your plan under Billing. <Badge>Free</Badge> and <Badge>Starter</Badge> include
            email support; <Badge tone="success">Growth</Badge> and above add live chat. See the
            Support page to reach us.
          </Text>
        </Section>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Frequently asked questions
            </Text>
            <Divider />
            <FaqItem id="faq-not-showing" question="The game doesn't appear on my store.">
              Make sure the campaign is Published, the App embed is enabled (Online Store → Themes →
              Customize → App embeds → PlayForge), and the current page matches your Rules. Then
              hard-refresh the storefront.
            </FaqItem>
            <Divider />
            <FaqItem id="faq-code-checkout" question="A won code shows but doesn't work at checkout.">
              The discount must be created in Shopify, which needs the app connected with discount
              permissions. After connecting, new wins create real codes — check Discounts in your
              Shopify admin.
            </FaqItem>
            <Divider />
            <FaqItem id="faq-conversion-zero" question="Redemptions/conversion stay at 0 after an order.">
              Open Coupons → Sync orders (Analytics and Customers also sync automatically when
              opened). PlayForge reconciles redemptions by reading discount usage from Shopify.
            </FaqItem>
            <Divider />
            <FaqItem id="faq-multiple-plays" question="Can a shopper play multiple times by clearing their browser?">
              No — enforcement is server-side, per email (and per customer for logged-in shoppers).
              A guest using a brand-new email is the inherent limit of anonymous play.
            </FaqItem>
            <Divider />
            <FaqItem id="faq-why-email" question="Why do you ask shoppers for an email?">
              To enforce one play per shopper and to lock the won discount to that specific customer
              (created in Shopify if new) with a single use.
            </FaqItem>
            <Divider />
            <FaqItem id="faq-discount-types" question="Which prizes become real Shopify codes?">
              Percentage, fixed amount, free shipping, and free product. Informational outcomes
              (no win / try again) produce no code.
            </FaqItem>
            <Divider />
            <FaqItem id="faq-wallet" question="Where do shoppers see the codes they've won?">
              In their account, under <b>My Rewards</b>. New customer accounts get the page
              automatically (enable the extension and set its API base URL); classic accounts need
              the <b>PlayForge Rewards</b> block added to the Account template. Each coupon shows its
              status, expiry, and a show/copy control, with status filters and soonest-expiry sort.
            </FaqItem>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd">
              Still need help?
            </Text>
            <Text as="p" tone="subdued">
              Reach our team from the Support page — email on every plan, live chat on Growth and
              above.
            </Text>
            <InlineStack>
              <Button variant="primary" onClick={() => navigate('/support')}>
                Contact support
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
